import React, { useState, useContext, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '../components/ui/dialog';
import axios from 'axios';
import { toast } from 'sonner';
import { Bookmark, History, LogOut, Shield, Loader2, Download } from 'lucide-react';
import { AuthContext } from '../App';
// jsPDF import removed - now using backend PDF generation

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Dashboard = () => {
  const navigate = useNavigate();
  const { user, logout, hasAccess, checkAuth } = useContext(AuthContext);
  const [chemicalName, setChemicalName] = useState('');
  const [analysis, setAnalysis] = useState(null);
  const [loading, setLoading] = useState(false);
  const [showSubscribeModal, setShowSubscribeModal] = useState(false);
  const [processingPayment, setProcessingPayment] = useState(false);
  const [suggestions, setSuggestions] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);

  useEffect(() => {
    checkAuth();
    
    // Check for demo chemical from landing page
    const demoChemical = localStorage.getItem('demo_chemical');
    if (demoChemical) {
      setChemicalName(demoChemical);
      localStorage.removeItem('demo_chemical');
      // Auto-trigger analysis after a short delay
      setTimeout(() => {
        handleAnalyze();
      }, 1000);
    }
  }, []);

  // Fetch autocomplete suggestions
  const fetchSuggestions = async (query) => {
    if (query.length < 2) {
      setSuggestions([]);
      setShowSuggestions(false);
      return;
    }

    try {
      const response = await axios.get(`${API}/chemical/autocomplete?q=${encodeURIComponent(query)}`);
      setSuggestions(response.data.suggestions || []);
      setShowSuggestions(response.data.suggestions.length > 0);
    } catch (error) {
      console.error('Autocomplete error:', error);
    }
  };

  const handleInputChange = (e) => {
    const value = e.target.value;
    setChemicalName(value);
    fetchSuggestions(value);
  };

  const handleSuggestionClick = (suggestion) => {
    setChemicalName(suggestion);
    setShowSuggestions(false);
    setSuggestions([]);
  };

  const handleAnalyze = async () => {
    if (!chemicalName.trim()) {
      toast.error('Please enter a chemical name');
      return;
    }

    setLoading(true);
    setAnalysis(null);

    try {
      const response = await axios.post(
        `${API}/chemical/analyze`,
        { chemical_name: chemicalName },
        { withCredentials: true }
      );

      setAnalysis(response.data);
      toast.success('Analysis complete!');
    } catch (error) {
      if (error.response?.status === 403) {
        toast.error('Your trial has expired. Please subscribe to continue.');
        setShowSubscribeModal(true);
      } else {
        toast.error(error.response?.data?.detail || 'Analysis failed');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleBookmark = async () => {
    if (!analysis) return;

    try {
      await axios.post(
        `${API}/bookmarks/add`,
        {
          chemical_id: analysis.id,
          chemical_name: analysis.chemical_name,
          compatibility_status: analysis.compatibility_status,
          compatibility_color: analysis.compatibility_color
        },
        { withCredentials: true }
      );

      toast.success('Chemical bookmarked!');
    } catch (error) {
      toast.error('Failed to bookmark');
    }
  };

  const handleExportPDF = async () => {
    if (!analysis) {
      toast.error('No analysis data to export');
      return;
    }

    try {
      console.log('=== Starting PDF export ===');
      
      // Show loading toast
      const loadingToast = toast.loading('Generating PDF report...');
      
      // Call backend PDF generation endpoint
      const response = await axios.get(
        `${API}/chemical/analysis/${analysis.id}/pdf`,
        {
          responseType: 'blob',
          withCredentials: true
        }
      );
      
      // Create blob URL for download
      const pdfBlob = new Blob([response.data], { type: 'application/pdf' });
      const blobUrl = URL.createObjectURL(pdfBlob);
      
      // Generate filename
      const safeChemicalName = analysis.chemical_name.replace(/[^a-z0-9]/gi, '_');
      const filename = `chemical_analysis_${safeChemicalName}_${analysis.id.substring(0, 8)}.pdf`;
      
      // Create and auto-click download link
      const link = document.createElement('a');
      link.href = blobUrl;
      link.download = filename;
      link.style.display = 'none';
      document.body.appendChild(link);
      link.click();
      
      // Cleanup
      setTimeout(() => {
        document.body.removeChild(link);
        URL.revokeObjectURL(blobUrl);
      }, 1000);
      
      // Dismiss loading toast and show success
      toast.dismiss(loadingToast);
      toast.success('✓ PDF report downloaded successfully!', { duration: 5000 });
      
      console.log('=== PDF export complete ===');
      
    } catch (error) {
      console.error('=== PDF export FAILED ===', error);
      
      // Handle specific error cases
      if (error.response?.status === 401) {
        toast.error('Please log in to download PDF reports');
      } else if (error.response?.status === 403) {
        toast.error('Your trial has expired. Please subscribe to download PDF reports.');
      } else if (error.response?.status === 404) {
        toast.error('Analysis not found. Please try analyzing the chemical again.');
      } else {
        toast.error('Failed to generate PDF report. Please try again.');
      }
    }
  };

  const handleSubscribe = async () => {
    setProcessingPayment(true);
    try {
      const originUrl = window.location.origin;
      const response = await axios.post(
        `${API}/payments/checkout/session`,
        {},
        {
          headers: {
            'Origin': originUrl
          },
          withCredentials: true
        }
      );

      window.location.href = response.data.url;
    } catch (error) {
      toast.error('Failed to create checkout session');
      setProcessingPayment(false);
    }
  };

  const getStatusClass = (color) => {
    if (color === 'red') return 'status-red';
    if (color === 'yellow') return 'status-yellow';
    if (color === 'green') return 'status-green';
    return 'bg-gray-500';
  };

  const getStatusLabel = (status) => {
    if (status === 'not_compatible') return 'NOT COMPATIBLE';
    if (status === 'compatible') return 'COMPATIBLE';
    if (status === 'caution') return 'MERIT CAUTION / CONDITIONALLY COMPATIBLE';
    return status;
  };

  return (
    <div className="min-h-screen" style={{ background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)' }}>
      {/* Header */}
      <header className="glass sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 py-4 flex items-center justify-between">
          <h1 className="text-2xl font-bold text-white" style={{ fontFamily: 'Space Grotesk' }}>
            Chem-Steel 316L
          </h1>
          <div className="flex items-center gap-4">
            {!hasAccess && user?.role !== 'admin' && (
              <span className="text-xs text-white/80 bg-white/20 px-3 py-1 rounded-full">
                Trial Expired
              </span>
            )}
            {user?.role === 'admin' && (
              <Button
                data-testid="admin-panel-button"
                onClick={() => navigate('/admin')}
                variant="ghost"
                className="text-white hover:bg-white/20"
              >
                <Shield className="w-4 h-4 mr-2" />
                Admin
              </Button>
            )}
            <Button
              data-testid="history-button"
              onClick={() => navigate('/history')}
              variant="ghost"
              className="text-white hover:bg-white/20"
            >
              <History className="w-4 h-4 mr-2" />
              History
            </Button>
            <Button
              data-testid="bookmarks-button"
              onClick={() => navigate('/bookmarks')}
              variant="ghost"
              className="text-white hover:bg-white/20"
            >
              <Bookmark className="w-4 h-4 mr-2" />
              Bookmarks
            </Button>
            <Button
              data-testid="logout-button"
              onClick={logout}
              variant="ghost"
              className="text-white hover:bg-white/20"
            >
              <LogOut className="w-4 h-4 mr-2" />
              Logout
            </Button>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-5xl mx-auto px-4 py-12 pb-32 space-y-8">{/* Added pb-32 for bottom padding */}
        {/* Search Section */}
        <Card className="glass border-white/20" data-testid="search-card">
          <CardHeader>
            <CardTitle className="text-3xl text-white" style={{ fontFamily: 'Space Grotesk' }}>
              Chemical Compatibility Analysis
            </CardTitle>
            <CardDescription className="text-white/80">
              Enter a chemical name to analyze its compatibility with Stainless Steel 316L
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex gap-3 relative">
              <div className="flex-1 relative">
                <Input
                  data-testid="chemical-input"
                  placeholder="e.g., Hydrochloric Acid, Sodium Hydroxide..."
                  value={chemicalName}
                  onChange={handleInputChange}
                  onKeyPress={(e) => e.key === 'Enter' && handleAnalyze()}
                  onBlur={() => setTimeout(() => setShowSuggestions(false), 200)}
                  onFocus={() => suggestions.length > 0 && setShowSuggestions(true)}
                  className="flex-1 bg-white/10 border-white/20 text-white placeholder:text-white/50 text-lg py-6"
                />
                {showSuggestions && suggestions.length > 0 && (
                  <div className="absolute z-50 w-full mt-1 bg-white rounded-lg shadow-lg max-h-60 overflow-y-auto">
                    {suggestions.map((suggestion, index) => (
                      <div
                        key={index}
                        onClick={() => handleSuggestionClick(suggestion)}
                        className="px-4 py-3 hover:bg-purple-100 cursor-pointer text-gray-800 border-b last:border-b-0"
                      >
                        {suggestion}
                      </div>
                    ))}
                  </div>
                )}
              </div>
              <Button
                data-testid="analyze-button"
                onClick={handleAnalyze}
                disabled={loading || (!hasAccess && user?.role !== 'admin')}
                size="lg"
                className="bg-white text-purple-700 hover:bg-gray-100 px-8 btn-hover"
              >
                {loading ? (
                  <>
                    <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                    Analyzing...
                  </>
                ) : (
                  'Analyze'
                )}
              </Button>
            </div>
            {!hasAccess && user?.role !== 'admin' && (
              <div className="bg-yellow-500/20 border border-yellow-500/40 rounded-lg p-4">
                <p className="text-white text-sm">
                  Your trial has expired. <button onClick={() => setShowSubscribeModal(true)} className="underline font-semibold">Subscribe now</button> to continue analyzing chemicals.
                </p>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Results Section */}
        {analysis && (
          <div className="space-y-6 fade-in-up" data-testid="analysis-results">
            {/* Compatibility Status Box */}
            <Card className={`${getStatusClass(analysis.compatibility_color)} border-0 shadow-2xl card-hover`}>
              <CardHeader>
                <CardTitle className="text-2xl flex items-center justify-between">
                  <span>{getStatusLabel(analysis.compatibility_status)}</span>
                  <div className="flex gap-2">
                    <Button
                      data-testid="export-pdf-button"
                      onClick={handleExportPDF}
                      variant="ghost"
                      className="hover:bg-white/20 flex items-center gap-2"
                    >
                      <Download className="w-5 h-5" />
                      <span className="hidden sm:inline">Export PDF</span>
                    </Button>
                    <Button
                      data-testid="bookmark-button"
                      onClick={handleBookmark}
                      variant="ghost"
                      className="hover:bg-white/20"
                    >
                      <Bookmark className="w-5 h-5" />
                    </Button>
                  </div>
                </CardTitle>
              </CardHeader>
            </Card>

            {/* Detailed Analysis Report */}
            <Card className="glass border-white/20">
              <CardHeader>
                <CardTitle className="text-2xl text-white" style={{ fontFamily: 'Space Grotesk' }}>
                  Detailed Analysis Report
                </CardTitle>
                <CardDescription className="text-white/80">
                  AI-powered analysis with web-sourced data • Always verify with official Safety Data Sheets
                </CardDescription>
                {analysis.cas_number && (
                  <div className="mt-2 flex items-center gap-2 text-white/90">
                    <span className="font-semibold">CAS Number:</span>
                    <span className="bg-white/20 px-3 py-1 rounded-full text-sm">{analysis.cas_number}</span>
                  </div>
                )}
              </CardHeader>
              <CardContent>
                <div className="prose prose-invert max-w-none">
                  <div className="scannable-text text-white/90 whitespace-pre-wrap" data-testid="analysis-report">
                    {analysis.analysis_report}
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        )}
      </main>

      {/* Footer */}
      <footer className="glass mt-12 border-t border-white/20">
        <div className="max-w-7xl mx-auto px-4 py-6">
          <div className="flex flex-col md:flex-row justify-between items-center gap-4 text-white/80 text-sm">
            <div className="flex items-center gap-2">
              <span>© {new Date().getFullYear()} Chem-Steel 316L Compatibility Checker</span>
            </div>
            
            <div className="flex flex-wrap items-center justify-center gap-4">
              <Link 
                to="/whats-new" 
                className="hover:text-white underline"
                data-testid="whats-new-link"
              >
                What's New
              </Link>
              <span>•</span>
              <Link 
                to="/faq" 
                className="hover:text-white underline"
                data-testid="faq-link"
              >
                FAQ
              </Link>
              <span>•</span>
              <Link 
                to="/terms" 
                className="hover:text-white underline"
                data-testid="terms-link"
              >
                Terms of Service
              </Link>
              <span>•</span>
              <Link 
                to="/privacy" 
                className="hover:text-white underline"
                data-testid="privacy-link"
              >
                Privacy Policy
              </Link>
              <span>•</span>
              <a 
                href="mailto:info@corrosioncheck.com" 
                className="hover:text-white underline"
                data-testid="contact-link"
              >
                Contact: info@corrosioncheck.com
              </a>
            </div>
          </div>
          
          <div className="text-center mt-4 text-white/60 text-xs">
            <p>
              <strong>Disclaimer:</strong> This service provides informational analysis only. 
              Always verify compatibility with official Safety Data Sheets and consult qualified professionals 
              before handling chemicals or making material selection decisions.
            </p>
          </div>
        </div>
      </footer>

      {/* Subscribe Modal */}
      <Dialog open={showSubscribeModal} onOpenChange={setShowSubscribeModal}>
        <DialogContent className="bg-white">
          <DialogHeader>
            <DialogTitle className="text-2xl" style={{ fontFamily: 'Space Grotesk' }}>Subscribe to Continue</DialogTitle>
            <DialogDescription>
              Your 10-day free trial has ended. Subscribe now to continue accessing unlimited chemical analyses.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-6 py-4">
            <div className="bg-purple-50 p-6 rounded-lg space-y-3">
              <h3 className="text-xl font-semibold">Individual Account</h3>
              <p className="text-3xl font-bold text-purple-700">£19.00<span className="text-base font-normal text-gray-600">/month</span></p>
              <ul className="space-y-2 text-sm text-gray-700">
                <li>✓ Unlimited chemical analyses</li>
                <li>✓ AI-powered compatibility reports with Claude Sonnet 4</li>
                <li>✓ Save and bookmark chemicals</li>
                <li>✓ Search history tracking</li>
                <li>✓ Detailed compatibility reports with source citations</li>
                <li>✓ Transport code information (IMDG, ADR, USDOT)</li>
                <li>✓ Web-sourced data verification</li>
                <li>✓ PDF export functionality</li>
              </ul>
            </div>
            
            <div className="bg-blue-50 p-6 rounded-lg space-y-3 border-2 border-blue-500">
              <div className="flex items-center justify-between">
                <h3 className="text-xl font-semibold">Business Account</h3>
                <span className="bg-blue-500 text-white text-xs px-2 py-1 rounded-full">BEST VALUE</span>
              </div>
              <p className="text-3xl font-bold text-blue-700">£3,500<span className="text-base font-normal text-gray-600">/year</span></p>
              <p className="text-sm text-gray-600">Up to 20 users • Save £1,060/year</p>
              <ul className="space-y-2 text-sm text-gray-700">
                <li>✓ All Individual features</li>
                <li>✓ 20 user accounts</li>
                <li>✓ Business admin dashboard</li>
                <li>✓ Add/remove team members</li>
                <li>✓ Team usage analytics</li>
                <li>✓ Priority support</li>
                <li>✓ Single annual invoice</li>
              </ul>
              <Button
                onClick={() => window.location.href = 'mailto:info@corrosioncheck.com?subject=Business Account Inquiry&body=I am interested in the Business Account for 20 users at £3,500/year. Please contact me with payment details.'}
                className="w-full bg-blue-600 hover:bg-blue-700 text-white py-3 text-sm"
              >
                Contact Sales for Business
              </Button>
            </div>
            
            <Button
              data-testid="subscribe-button"
              onClick={handleSubscribe}
              disabled={processingPayment}
              className="w-full bg-purple-700 hover:bg-purple-800 text-white py-6 text-lg"
            >
              {processingPayment ? (
                <>
                  <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                  Processing...
                </>
              ) : (
                'Subscribe Individual - £19/month'
              )}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Dashboard;
